import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Plus, Trash2, Edit2, X, Check, Bell, Link, Clock } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  text: '',
  display_time: 10,
  link_url: '',
  link_name: '',
  is_active: true,
};

export default function NotificationBarSettings({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const canManage = user?.role === 'super_admin' || user?.can_manage_global_notifications;

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/global-notifications`);
      setNotifications(res.data || []);
    } catch {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!form.text.trim()) {
      toast.error('Notification text is required');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API_URL}/global-notifications/${editingId}`, form);
        toast.success('Notification updated');
      } else {
        await axios.post(`${API_URL}/global-notifications`, form);
        toast.success('Notification created');
      }
      await fetchNotifications();
      resetForm();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save notification');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (notif) => {
    setForm({
      text: notif.text || '',
      display_time: notif.display_time || 10,
      link_url: notif.link_url || '',
      link_name: notif.link_name || '',
      is_active: notif.is_active !== false,
    });
    setEditingId(notif.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await axios.delete(`${API_URL}/global-notifications/${id}`);
      toast.success('Notification deleted');
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch {
      toast.error('Failed to delete notification');
    }
  };

  const handleToggleActive = async (notif) => {
    try {
      await axios.put(`${API_URL}/global-notifications/${notif.id}`, {
        is_active: !notif.is_active,
      });
      setNotifications(prev =>
        prev.map(n => n.id === notif.id ? { ...n, is_active: !n.is_active } : n)
      );
    } catch {
      toast.error('Failed to update notification');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 text-sm">Loading notifications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Global Notification Bar</h2>
          <p className="text-sm text-gray-500 mt-1">
            Messages broadcast to all logged-in users. Multiple notifications rotate automatically.
          </p>
        </div>
        {canManage && !showForm && (
          <Button
            data-testid="add-notification-btn"
            onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
            style={{ backgroundColor: '#ee7203', color: 'white' }}
            className="gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Notification
          </Button>
        )}
      </div>

      {/* Add / Edit Form */}
      {showForm && canManage && (
        <Card className="border-2" style={{ borderColor: '#FCD34D' }}>
          <CardHeader className="pb-3" style={{ backgroundColor: '#FFFBEB' }}>
            <CardTitle className="text-sm font-semibold text-amber-800 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              {editingId ? 'Edit Notification' : 'New Notification'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-gray-700">
                Message Text <span className="text-red-500">*</span>
              </Label>
              <Input
                data-testid="notification-text-input"
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                placeholder="e.g. System maintenance scheduled for tonight at 10pm"
                className="text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Display Time (seconds)
                </Label>
                <Input
                  data-testid="notification-display-time-input"
                  type="number"
                  min={3}
                  max={300}
                  value={form.display_time}
                  onChange={e => setForm(f => ({ ...f, display_time: parseInt(e.target.value) || 10 }))}
                  className="text-sm"
                />
              </div>

              <div className="space-y-1.5 flex flex-col justify-end">
                <div className="flex items-center gap-2 pb-1">
                  <Switch
                    id="notif-active"
                    checked={form.is_active}
                    onCheckedChange={val => setForm(f => ({ ...f, is_active: val }))}
                    data-testid="notification-active-switch"
                  />
                  <Label htmlFor="notif-active" className="text-sm cursor-pointer">
                    {form.is_active ? 'Active (visible in bar)' : 'Inactive (hidden)'}
                  </Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                  <Link className="w-3 h-3" /> Link URL (optional)
                </Label>
                <Input
                  data-testid="notification-link-url-input"
                  value={form.link_url}
                  onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
                  placeholder="https://..."
                  className="text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-gray-700">
                  Link Label (optional)
                </Label>
                <Input
                  data-testid="notification-link-name-input"
                  value={form.link_name}
                  onChange={e => setForm(f => ({ ...f, link_name: e.target.value }))}
                  placeholder="e.g. View Details"
                  className="text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={resetForm} className="gap-1">
                <X className="w-3.5 h-3.5" /> Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={saving}
                data-testid="notification-save-btn"
                style={{ backgroundColor: '#ee7203', color: 'white' }}
                className="gap-1"
              >
                <Check className="w-3.5 h-3.5" />
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Notifications List */}
      {notifications.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm border-2 border-dashed rounded-lg">
          No notifications yet. {canManage ? 'Click "Add Notification" to create one.' : ''}
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map(notif => (
            <Card
              key={notif.id}
              data-testid={`notification-card-${notif.id}`}
              className="border"
              style={{
                borderLeftWidth: '4px',
                borderLeftColor: notif.is_active ? '#F59E0B' : '#D1D5DB',
                opacity: notif.is_active ? 1 : 0.65,
              }}
            >
              <CardContent className="py-3 px-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div
                      className="font-medium text-sm text-gray-900 truncate"
                      data-testid={`notification-text-${notif.id}`}
                    >
                      {notif.text}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {notif.display_time || 10}s
                      </span>
                      {notif.link_url && (
                        <span className="text-xs text-blue-600 flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          {notif.link_name || notif.link_url}
                        </span>
                      )}
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor: notif.is_active ? '#FEF3C7' : '#F3F4F6',
                          color: notif.is_active ? '#92400E' : '#6B7280',
                        }}
                      >
                        {notif.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Switch
                        data-testid={`notification-toggle-${notif.id}`}
                        checked={notif.is_active}
                        onCheckedChange={() => handleToggleActive(notif)}
                        title={notif.is_active ? 'Deactivate' : 'Activate'}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-500 hover:text-orange-600"
                        data-testid={`notification-edit-${notif.id}`}
                        onClick={() => handleEdit(notif)}
                        title="Edit"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-gray-500 hover:text-red-600"
                        data-testid={`notification-delete-${notif.id}`}
                        onClick={() => handleDelete(notif.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview banner */}
      {notifications.some(n => n.is_active) && (
        <div className="rounded-lg overflow-hidden border border-amber-200">
          <div className="px-3 py-1 bg-amber-50 border-b border-amber-200">
            <span className="text-xs font-semibold text-amber-700">Preview — bar as users see it</span>
          </div>
          <div
            style={{
              backgroundColor: '#FDE68A',
              borderBottom: '2px solid #F59E0B',
              padding: '7px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#78350F' }}>
              {notifications.find(n => n.is_active)?.text || ''}
            </span>
            {notifications.find(n => n.is_active)?.link_url && (
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#92400E', textDecoration: 'underline' }}>
                {notifications.find(n => n.is_active)?.link_name || 'View'}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
